export type TestFamily =
  | "discrimination"
  | "descriptive"
  | "consumer"
  | "temporal"
  | "threshold";

export type TestStatus = "complete" | "active" | "scheduled" | "draft";

export type SensoryTest = {
  id: string;
  family: TestFamily;
  method: string;
  title: string;
  status: TestStatus;
  panel: number;
  alpha?: number;
  beta?: number;
  when: string;
  // Family-specific extras
  correct?: number;
  dprime?: number;
  pValue?: number;
  verdict?: "different" | "same";
  served?: string;
  overall?: number;
  byAttr?: Record<string, number>;
  training?: number;
  replicates?: number;
};

export const TEST_FAMILY_META: Record<
  TestFamily,
  { label: string; short: string; tone: string }
> = {
  discrimination: { label: "Discrimination", short: "DISC", tone: "var(--brand-amber)" },
  descriptive:    { label: "Descriptive",    short: "DESC", tone: "var(--brand-green)" },
  consumer:       { label: "Consumer",       short: "CONS", tone: "var(--fam-floral)" },
  temporal:       { label: "Temporal",       short: "TEMP", tone: "var(--fam-citrus)" },
  threshold:      { label: "Threshold",      short: "THR",  tone: "var(--fam-roast)" },
};

export const STATUS_TONE: Record<
  TestStatus,
  { bg: string; line: string; fg: string; lbl: string }
> = {
  complete:  { bg: "var(--accent-soft)", line: "var(--accent-line)", fg: "var(--accent-ink)", lbl: "COMPLETE" },
  active:    { bg: "var(--signal-soft)", line: "#e7c97a",            fg: "#7c5813",           lbl: "RUNNING"  },
  scheduled: { bg: "var(--bg-sunk)",     line: "var(--line)",        fg: "var(--ink-3)",      lbl: "SCHEDULED"},
  draft:     { bg: "var(--panel-2)",     line: "var(--line)",        fg: "var(--ink-4)",      lbl: "DRAFT"    },
};

export const TEST_QUEUE: SensoryTest[] = [
  {
    id: "t-tri-r2",
    family: "discrimination",
    method: "Triangle (ISO 4120)",
    title: "Match vs. incumbent · v2",
    status: "complete",
    panel: 36,
    correct: 21,
    alpha: 0.05,
    beta: 0.20,
    dprime: 1.42,
    pValue: 0.018,
    verdict: "different",
    served: "balanced AAB/ABA/BAA, n=36",
    when: "Apr 26 · 2 days ago",
  },
  {
    id: "t-dfc-r2",
    family: "discrimination",
    method: "Difference-from-control",
    title: "Magnitude of drift · v2 vs gold",
    status: "complete",
    panel: 24,
    alpha: 0.05,
    overall: 2.6,
    byAttr: { aroma: 3.1, flavor: 2.4, mouthfeel: 1.2, aftertaste: 3.0 },
    when: "Apr 26",
  },
  {
    id: "t-qda-r2",
    family: "descriptive",
    method: "QDA",
    title: "Round 2 · trained panel · n=11",
    status: "active",
    panel: 11,
    training: 48,
    replicates: 3,
    when: "Apr 27 — 28",
  },
  {
    id: "t-cons-r2",
    family: "consumer",
    method: "9-pt hedonic + JAR + PI",
    title: "CLT · n=148 · category users",
    status: "active",
    panel: 148,
    alpha: 0.05,
    when: "Apr 28 — May 02",
  },
  {
    id: "t-tds-r2",
    family: "temporal",
    method: "TDS",
    title: "Sweetener carry-through",
    status: "scheduled",
    panel: 9,
    when: "May 04",
  },
  {
    id: "t-thr-r1",
    family: "threshold",
    method: "Best Estimate Threshold",
    title: "γ-decalactone in dairy base",
    status: "scheduled",
    panel: 12,
    when: "May 06",
  },
];
